import { PrefKey } from '@sdk';
import { usePreference } from '@src/hooks/use-preference';
import { useDefaultWorkerType } from '@src/contexts/HarnessCapabilitiesContext';
import { rememberLaunch } from './last-launch';
import { type OpenerId } from './tab_opener_types';
import type { WorkerType } from '@src/components/workers/worker-types';

/**
 * Single source of truth for the "last opener / last worker" the user launched.
 *
 * The terminal strip (`usePinnedOpeners`) remembers the last *opener* (a broader
 * set: `claude` / `codex` / `copilot` / `terminal` / `sandbox` / …), while the
 * shared `ContextWorkerToolbar` cares only about the three worker vendors. Both read and
 * write this one preference (`PrefKey.LAST_OPENER`) so launching Claude from
 * either surface keeps the other in sync; prefMan handles reactivity.
 */

// Opener ids and worker types name the same vendors differently (`claude` vs
// `claude_code`); these two maps are the only place that bridges them.
const OPENER_TO_WORKER: Partial<Record<OpenerId, WorkerType>> = {
  claude: 'claude_code',
  codex: 'codex',
  copilot: 'copilot',
  opencode: 'opencode',
};

// `as const satisfies` rather than a plain `Record<WorkerType, OpenerId>`
// annotation: the checked shape is the same, but the values stay LITERAL, so
// `workerToOpener` can hand back the four vendor ids rather than the whole
// opener union (which also carries `terminal` / `sandbox` and can't index a
// vendor-keyed table).
const WORKER_TO_OPENER = {
  claude_code: 'claude',
  codex: 'codex',
  copilot: 'copilot',
  opencode: 'opencode',
} as const satisfies Record<WorkerType, OpenerId>;

/** The opener ids that name a worker vendor — the image of {@link workerToOpener}. */
export type WorkerOpenerId = (typeof WORKER_TO_OPENER)[WorkerType];

/** Coerce a stored opener id to a worker vendor — null for non-worker openers. */
export function openerToWorker(id: OpenerId | null): WorkerType | null {
  return id ? OPENER_TO_WORKER[id] ?? null : null;
}

/** Coerce a worker vendor to its opener id (for shared persistence). */
export function workerToOpener(worker: WorkerType): WorkerOpenerId {
  return WORKER_TO_OPENER[worker];
}

function rememberWorker(worker: WorkerType): void {
  rememberLaunch(workerToOpener(worker), null);
}

export interface UseLastWorkerTypeResult {
  /** Last worker launched from any surface, or null if none / a non-worker opener. */
  lastWorker: WorkerType | null;
  /** Persist `worker` as the last opener (keeps the terminal strip in sync).
   *  The shape is unknown on these paths (task runners pin their own transport),
   *  so the quick-launch slot falls back to a terminal for it. */
  rememberWorker: (worker: WorkerType) => void;
}

/**
 * React view over the shared last-opener preference, scoped to worker vendors.
 * Stays live as either the toolbar or the terminal strip writes the key.
 */
export function useLastWorkerType(): UseLastWorkerTypeResult {
  const [lastOpener] = usePreference<OpenerId | null>(PrefKey.LAST_OPENER);
  const lastWorker = openerToWorker(lastOpener);
  return { lastWorker, rememberWorker };
}

/** The worker a launch surface leads with: the last one launched, else the capability default. */
export function usePrimaryWorkerType(): WorkerType {
  const { lastWorker } = useLastWorkerType();
  const defaultWorker = useDefaultWorkerType();
  return lastWorker ?? defaultWorker;
}
